/**
 * TrialJourneyPage — visual preview of every day-stage in the 14-day free trial.
 * Self-contained: no BillingContext required. All components are rendered inline
 * using props so you can see exactly what the user sees at each stage.
 */
import React, { useState } from 'react';
import { formatAnnual, formatMonthly, savingPct, sellablePlans, usePlanCatalogue } from '../planPrices';
import { Lock, Mail, Bell, MessageSquare, Settings } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import s from './TrialJourneyPage.module.css';

// ─── Standalone preview components (mirror real components, no context) ───────

const BannerPreview: React.FC<{ daysLeft: number; expired?: boolean }> = ({ daysLeft, expired }) => {
  const pct = Math.round(((14 - daysLeft) / 14) * 100);
  const urgency = daysLeft <= 3 ? s.urgent : daysLeft <= 7 ? s.warning : s.info;

  if (expired) {
    return (
      <div className={`${s.banner} ${s.expired}`}>
        <span>Your free trial has expired.</span>
        <button className={s.bannerCta}>Upgrade Now</button>
      </div>
    );
  }

  return (
    <div className={`${s.banner} ${urgency}`}>
      <div className={s.bannerLeft}>
        <span className={s.bannerLabel}>
          {daysLeft === 0
            ? 'Your trial expires today'
            : `${daysLeft} day${daysLeft !== 1 ? 's' : ''} left in your free trial`}
        </span>
        <div className={s.progressBar}>
          <div className={s.progressFill} style={{ width: `${pct}%` }} />
        </div>
      </div>
      <button className={s.bannerCta}>{daysLeft <= 3 ? 'Subscribe Now' : 'See Plans'}</button>
    </div>
  );
};

const ModalPreview: React.FC<{ daysLeft: number }> = ({ daysLeft }) => {
  // The same catalogue the pricing page reads, so the preview cannot quote a stale price.
  const PLANS = sellablePlans(usePlanCatalogue()).map((p) => ({
    tier: p.tier, name: p.display_name,
    price: formatMonthly(p) ?? '—', annual: formatAnnual(p) ?? '—',
    seats: p.features.user_seats, popular: p.is_popular, saving: savingPct(p),
  }));
  return (
    <div className={s.modalWrap}>
      <div className={s.modal}>
        <button className={s.modalClose}>×</button>
        <h2 className={s.modalTitle}>
          {daysLeft <= 0 ? 'Your trial has ended' : `${daysLeft} days left — don't lose access`}
        </h2>
        <p className={s.modalSub}>Choose a plan to keep your regulatory intelligence flowing.</p>
        <div className={s.modalPlans}>
          {PLANS.map(p => (
            <div key={p.tier} className={`${s.modalCard} ${p.popular ? s.popular : ''}`}>
              {p.popular && <span className={s.popularBadge}>Most Popular</span>}
              <h3 className={s.modalPlanName}>{p.name}</h3>
              <div className={s.modalPrice}>{p.price}</div>
              <div className={s.modalAnnual}>or {p.annual}{p.saving !== null ? ` — save ${p.saving}%` : ''}</div>
              <div className={s.modalSeats}>{p.seats} users</div>
              <button className={s.modalSelectBtn}>Choose {p.name}</button>
            </div>
          ))}
        </div>
        <button className={s.modalDismiss}>Continue trial</button>
      </div>
    </div>
  );
};

const ExpiredPreview: React.FC = () => (
  <div className={s.expiredWrap}>
    <div className={s.expiredBox}>
      <div className={s.expiredIcon}><Lock size={20} aria-hidden /></div>
      <h2 className={s.expiredTitle}>Your free trial has ended</h2>
      <p className={s.expiredBody}>
        Your 14-day Policyora trial has expired. Subscribe to keep access to your
        policy monitoring, AI summaries, and alerts.
      </p>
      <button className={s.expiredCta}>Choose a Plan</button>
      <button className={s.expiredSecondary}>View Pricing</button>
    </div>
  </div>
);

const EmailPreview: React.FC<{ subject: string; preview: string }> = ({ subject, preview }) => (
  <div className={s.emailCard}>
    <div className={s.emailIcon}><Mail size={20} aria-hidden /></div>
    <div>
      <div className={s.emailSubject}>{subject}</div>
      <div className={s.emailPreview}>{preview}</div>
    </div>
  </div>
);

// ─── Stage definitions ────────────────────────────────────────────────────────

interface Stage {
  day: string;
  dayNum: number;
  label: string;
  tagline: string;
  color: string;
  emails?: { subject: string; preview: string }[];
  showBanner?: boolean;
  showModal?: boolean;
  showExpired?: boolean;
  daysLeft: number;
  appNote?: string;
}

const STAGES: Stage[] = [
  {
    day: 'Day 0',
    dayNum: 0,
    daysLeft: 14,
    label: 'Trial Starts',
    tagline: 'Full Professional access. No credit card. Trial banner appears.',
    color: 'var(--ds-link)',
    showBanner: true,
    appNote: 'User just completed signup / onboarding. ensureTrial() auto-creates subscription with status = TRIALING.',
  },
  {
    day: 'Day 3',
    dayNum: 3,
    daysLeft: 11,
    label: 'Getting Started Guidance',
    tagline: 'Early-trial guidance and onboarding prompts help the user settle in.',
    color: 'var(--ds-link)',
    showBanner: true,
    emails: [
      {
        subject: '3 things you can do with Policyora right now',
        preview: 'Track a jurisdiction, set up your first alert, and run an AI summary on any document — here\'s how.',
      },
    ],
    appNote: 'trial_service.send_drip_email_if_due() detects day === 3 and fires the tips email.',
  },
  {
    day: 'Day 7',
    dayNum: 7,
    daysLeft: 7,
    label: 'Feature Highlights + Reminder Banner',
    tagline: 'Mid-trial guidance appears and the banner shifts to a clearer warning state.',
    color: 'var(--ds-link)',
    showBanner: true,
    emails: [
      {
        subject: 'Have you tried advanced AI analysis yet?',
        preview: 'Export a regulation report, compare across jurisdictions, and invite your team — all before your trial ends.',
      },
    ],
    appNote: 'progressFill = 50%. Banner urgency switches to "warning" style (yellow).',
  },
  {
    day: 'Day 10',
    dayNum: 10,
    daysLeft: 4,
    label: 'Plan Reminder + Upgrade Prompt',
    tagline: 'A stronger upgrade prompt appears as the trial approaches its end.',
    color: 'var(--ds-link)',
    showBanner: true,
    showModal: true,
    emails: [
      {
        subject: '4 days left — lock in your plan today',
        preview: 'You\'ve been using Policyora for 10 days. Keep your alerts, history, and AI access — choose a plan.',
      },
    ],
    appNote: 'BillingContext detects trial_day >= 10 and opens ConversionPromptModal. Banner is now red (≤3 remaining counted from today).',
  },
  {
    day: 'Day 13',
    dayNum: 13,
    daysLeft: 1,
    label: 'Last Day Warning',
    tagline: '1 day left. Banner is red, CTA changes to "Subscribe Now".',
    color: 'var(--ds-danger-text)',
    showBanner: true,
    appNote: 'No new email on this day, but the banner is maximally urgent. progressFill = 92%.',
  },
  {
    day: 'Day 14',
    dayNum: 14,
    daysLeft: 0,
    label: 'Trial Expires Today',
    tagline: 'Banner says "expires today". By end-of-day subscription moves to EXPIRED.',
    color: 'var(--ds-danger-text)',
    showBanner: true,
    emails: [
      {
        subject: 'Your Policyora trial ends today',
        preview: 'After today your account will be in read-only mode. Subscribe now to keep full access.',
      },
    ],
    appNote: 'expire_stale_trials() job flips status to EXPIRED once trial_ends_at < now.',
  },
  {
    day: 'Post-trial',
    dayNum: 15,
    daysLeft: 0,
    label: 'Trial Expired — UI Locked',
    tagline: 'TrialExpiredOverlay covers the entire app. All routes are blocked.',
    color: 'var(--ds-danger-text)',
    showExpired: true,
    appNote: 'BillingContext sees status === expired → renders <TrialExpiredOverlay /> over AppLayout. User must subscribe to continue.',
  },
];

// ─── Page ─────────────────────────────────────────────────────────────────────

const TrialJourneyPage: React.FC = () => {
  const navigate = useNavigate();
  const [active, setActive] = useState<number>(0);
  const stage = STAGES[active];

  return (
    <div className={s.page}>
      {/* Header */}
      <header className={s.header}>
        <button className={s.back} onClick={() => navigate('/billing')}>← Back</button>
        <div>
          <span className={s.tag}>Trial Simulator</span>
          <h1 className={s.title}>14-Day Free Trial — What Users See</h1>
          <p className={s.sub}>Click any stage to preview the exact UI shown at that point.</p>
        </div>
      </header>

      <div className={s.layout}>
        {/* Sidebar — stage selector */}
        <aside className={s.sidebar}>
          {STAGES.map((st, i) => (
            <button
              key={st.day}
              className={`${s.stageBtn} ${active === i ? s.stageBtnActive : ''}`}
              onClick={() => setActive(i)}
              style={active === i ? { borderColor: st.color, color: st.color } : {}}
            >
              <span className={s.stageDot} style={{ background: st.color }} />
              <div className={s.stageBtnText}>
                <span className={s.stageBtnDay}>{st.day}</span>
                <span className={s.stageBtnLabel}>{st.label}</span>
              </div>
            </button>
          ))}
        </aside>

        {/* Main preview pane */}
        <main className={s.main}>
          {/* Stage heading */}
          <div className={s.stageHead}>
            <span className={s.stagePill} style={{ background: stage.color + '22', color: stage.color, border: `1px solid ${stage.color}55` }}>
              {stage.day}
            </span>
            <h2 className={s.stageTitle}>{stage.label}</h2>
            <p className={s.stageTagline}>{stage.tagline}</p>
          </div>

          {/* Progress bar visualiser */}
          <div className={s.progressSection}>
            <div className={s.progressTrack}>
              <div
                className={s.progressThumb}
                style={{ left: `${Math.min((stage.dayNum / 14) * 100, 100)}%`, background: stage.color }}
              />
              {[0,3,7,10,14].map(d => (
                <div key={d} className={s.progressMark} style={{ left: `${(d/14)*100}%` }}>
                  <span className={s.progressMarkLabel}>{d}</span>
                </div>
              ))}
            </div>
            <div className={s.progressLegend}>
              <span>Day 0</span><span>Day 14</span>
            </div>
          </div>

          {/* Email preview */}
          {stage.emails && stage.emails.length > 0 && (
            <div className={s.previewBlock}>
              <div className={s.previewLabel}><Mail size={16} aria-hidden /> Scheduled user communication for this stage</div>
              {stage.emails.map((em, i) => <EmailPreview key={i} {...em} />)}
            </div>
          )}

          {/* Banner preview */}
          {stage.showBanner && !stage.showExpired && (
            <div className={s.previewBlock}>
              <div className={s.previewLabel}><Bell size={16} aria-hidden /> Trial banner (top of every in-app page)</div>
              <div className={s.previewFrame}>
                <div className={s.mockAppBar}>
                  <span className={s.mockLogo}>Policyora</span>
                  <BannerPreview daysLeft={stage.daysLeft} />
                </div>
              </div>
            </div>
          )}

          {/* Conversion modal */}
          {stage.showModal && (
            <div className={s.previewBlock}>
              <div className={s.previewLabel}><MessageSquare size={16} aria-hidden /> ConversionPromptModal (shown on first app load, Day 10+)</div>
              <div className={s.previewFrame}>
                <ModalPreview daysLeft={stage.daysLeft} />
              </div>
            </div>
          )}

          {/* Expired overlay */}
          {stage.showExpired && (
            <div className={s.previewBlock}>
              <div className={s.previewLabel}><Lock size={16} aria-hidden /> TrialExpiredOverlay (full-screen, blocks entire app)</div>
              <div className={s.previewFrame}>
                <ExpiredPreview />
                <BannerPreview daysLeft={0} expired />
              </div>
            </div>
          )}

          {/* Dev note */}
          {stage.appNote && (
            <div className={s.devNote}>
              <span className={s.devNoteIcon}><Settings size={16} aria-hidden /></span>
              <span>{stage.appNote}</span>
            </div>
          )}

          {/* Next / Prev */}
          <div className={s.navBtns}>
            <button
              className={s.navBtn}
              onClick={() => setActive(i => Math.max(0, i - 1))}
              disabled={active === 0}
            >
              ← Previous
            </button>
            <button
              className={s.navBtn}
              onClick={() => setActive(i => Math.min(STAGES.length - 1, i + 1))}
              disabled={active === STAGES.length - 1}
            >
              Next →
            </button>
          </div>
        </main>
      </div>
    </div>
  );
};

export default TrialJourneyPage;
